import React, { useEffect, useState } from 'react'
import Icon from 'blocks.simple/icon/icon'
import { cn } from 'ethcss'
import { color } from 'theme'
import { shapeIconByKind } from '../templateEditor.data'
import { PlaceMode } from '../templateEditor.types'
import styles from './templateEditor__placeCursor.jcss'

const PLACE_CURSOR_OFFSET = 14

const iconTypeForPlaceTool = (placeMode: NonNullable<PlaceMode>): string => {
    switch (placeMode.type) {
        case 'text':
            return 'text'
        case 'shape':
            return shapeIconByKind[placeMode.opts.shapeKind ?? 'rect']
        case 'list':
            return 'listHorizontal'
        case 'listH':
            return 'listVertical'
        case 'table':
            return 'table'
        case 'video':
            return 'video'
        case 'image':
            return 'folder'
        default: {
            const _exhaustive: never = placeMode.type
            return _exhaustive
        }
    }
}

type TemplateEditorPlaceCursorProps = {
    placeMode: PlaceMode
    hidden?: boolean
}

export const TemplateEditorPlaceCursor = ({ placeMode, hidden = false }: TemplateEditorPlaceCursorProps) => {
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [tracked, setTracked] = useState(false)

    useEffect(() => {
        if (!placeMode) {
            setTracked(false)
            return
        }

        const onMove = (event: MouseEvent) => {
            setPosition({ x: event.clientX, y: event.clientY })
            setTracked(true)
        }

        const onLeave = () => setTracked(false)

        window.addEventListener('mousemove', onMove)
        document.documentElement.addEventListener('mouseleave', onLeave)

        return () => {
            window.removeEventListener('mousemove', onMove)
            document.documentElement.removeEventListener('mouseleave', onLeave)
        }
    }, [placeMode])

    if (!placeMode || !tracked) {
        return null
    }

    const thumbSrc =
        placeMode.type === 'image' || placeMode.type === 'video'
            ? placeMode.opts.thumbnail || placeMode.opts.src
            : undefined

    return (
        <div
            id="place-cursor"
            className={cn(
                styles.placeCursor,
                Boolean(thumbSrc) && styles.placeCursorMedia,
                hidden && styles.placeCursorHidden
            )}
            style={{
                transform: `translate3d(${position.x + PLACE_CURSOR_OFFSET}px, ${
                    position.y + PLACE_CURSOR_OFFSET
                }px, 0)`,
            }}
            aria-hidden="true"
        >
            {thumbSrc ? (
                <img className={styles.thumb} src={thumbSrc} alt="" draggable={false} />
            ) : (
                <Icon type={iconTypeForPlaceTool(placeMode)} size={14} color={color.blue} />
            )}
        </div>
    )
}
